<script setup lang="ts">
import type { ColumnDef } from '@tanstack/vue-table'
import type { components } from '#open-fetch-schemas/api'

const { $ts: t } = useNuxtApp()

const { tournamentWithStats } = defineProps({
  tournamentWithStats: {
    type: Object as PropType<Tournament>,
    required: true,
  },
})

const { data: results } = await useApi('/tournaments/{tournament}/results', {
  path: {
    tournament: tournamentWithStats.id,
  },
  key: `tournament-results-${tournamentWithStats.id}`,
})

const columns: ColumnDef<components['schemas']['TournamentGroupResults']['results'][number]>[] = [
  { header: t('Place'), accessorKey: 'place' },
  { header: t('Name'), accessorKey: 'name' },
  { header: t('Games played'), accessorKey: 'games_played' },
  { header: t('Games won'), accessorKey: 'games_won' },
  { header: t('Low visits'), accessorKey: 'low_visits' },
  { header: t('180s'), accessorKey: 'one_eighties' },
  { header: t('171s'), accessorKey: 'one_seven_ones' },
  { header: t('High checkouts'), accessorKey: 'high_checkouts' },
  { header: t('Highest checkout'), accessorKey: 'highest_checkout' },
  { header: t('Average'), accessorKey: 'average' },
]
</script>

<template>
  <div class="flex flex-col gap-4 flex-1">
    <UiAlert
      v-if="!tournamentWithStats.stats"
      variant="warning"
      :title="$t('Full results will become available after the tournament has been completed')"
    />
    <template v-else-if="tournamentWithStats.stats">
      <UiAlert
        v-if="tournamentWithStats.ranking_id"
        :title="$t('Ranking status')"
        :description="$t('This tournament is part of the {ranking} ranking you can view the ranking using the button below.', { ranking: tournamentWithStats.ranking_name })"
      >
        <template #action>
          <UiButton
            :text="$t('View ranking')"
            :to="$localePath(`/rankings/${tournamentWithStats.ranking_id}`)"
          />
        </template>
      </UiAlert>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <UiCard
          :title="tournamentWithStats.stats.winner"
          :description="$t('The winner of the tournament')"
        />
        <UiCard
          :title="tournamentWithStats.stats.total_games.toString()"
          :description="$t('Games played')"
        />
        <UiCard
          :title="tournamentWithStats.stats.one_eighties.toString()"
          :description="$t('180\'s')"
        />
        <UiCard
          :title="tournamentWithStats.stats.one_seven_ones.toString()"
          :description="$t('171\'s')"
        />
        <UiCard
          v-if="tournamentWithStats.stats.darts_thrown > 0"
          :title="tournamentWithStats.stats.darts_thrown.toString()"
          :description="$t('Darts thrown')"
        />
      </div>
    </template>
    <template v-if="results">
      <template
        v-for="(group, index) in results"
        :key="index"
      >
        <h3 class="font-bold text-lg">
          {{ group.label }}
        </h3>
        <UiTableReactiveTable
          :columns="columns"
          :data="group.results"
        />
      </template>
    </template>
  </div>
</template>
